import { z } from 'zod';

const ALLOWED_MIME_TYPES = [
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/x-matroska',
] as const;

export const VideoInstructionMimeSchema = z.enum(ALLOWED_MIME_TYPES);
export type VideoInstructionMime = z.infer<typeof VideoInstructionMimeSchema>;

export const VideoInstructionDtoSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string().nullable(),
  /** Public URL — opens from anywhere without auth. */
  publicUrl: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  durationSec: z.number().int().nonnegative().nullable(),
  displayOrder: z.number().int(),
  createdAt: z.string(),
  /** Convenience for the admin list — full name only, for display. */
  uploadedByName: z.string(),
});
export type VideoInstructionDto = z.infer<typeof VideoInstructionDtoSchema>;

/**
 * Request a presigned PUT URL for a fresh upload. The client uploads
 * the file directly to R2 (bypasses the API server's body-size limit
 * so we don't have to stream 100 MB+ through Fastify), then calls
 * `confirm` with the returned `storageKey` to commit the row.
 */
export const VideoInstructionPresignRequestSchema = z
  .object({
    fileName: z.string().trim().min(1).max(200),
    mimeType: VideoInstructionMimeSchema,
    /** Bytes; capped at 500 MB by the API for sanity. */
    sizeBytes: z
      .number()
      .int()
      .positive()
      .max(500 * 1024 * 1024),
  })
  .strict();
export type VideoInstructionPresignRequest = z.infer<
  typeof VideoInstructionPresignRequestSchema
>;

export const VideoInstructionPresignResponseSchema = z.object({
  url: z.string(),
  headers: z.record(z.string()),
  storageKey: z.string(),
  publicUrl: z.string(),
  expiresInSec: z.number().int().positive(),
});
export type VideoInstructionPresignResponse = z.infer<
  typeof VideoInstructionPresignResponseSchema
>;

export const VideoInstructionConfirmInputSchema = z
  .object({
    storageKey: z.string().min(1),
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().max(2000).optional(),
    mimeType: VideoInstructionMimeSchema,
    sizeBytes: z.number().int().positive(),
    /** Optional, set client-side from the <video> element after upload. */
    durationSec: z.number().int().positive().optional(),
  })
  .strict();
export type VideoInstructionConfirmInput = z.infer<
  typeof VideoInstructionConfirmInputSchema
>;

export const VideoInstructionUpdateInputSchema = z
  .object({
    title: z.string().trim().min(1).max(200).optional(),
    description: z.string().trim().max(2000).nullable().optional(),
    displayOrder: z.number().int().optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, {
    message: 'At least one field required',
  });
export type VideoInstructionUpdateInput = z.infer<
  typeof VideoInstructionUpdateInputSchema
>;
